import type { ToolCostVendor } from '../../../api/finance';

export interface VendorSpike {
  vendor: string;
  month: string;
  amount: number;
  /** Median of the vendor's earlier booked months. */
  usual: number;
}

/** A month at least this many times the vendor's usual one is an exception. */
export const SPIKE_FACTOR = 3;
/** Below this many euros above usual it is noise, however large the ratio. */
export const SPIKE_MIN_EXCESS_EUR = 100;
/** The vendor needs this many earlier booked months before "usual" means anything. */
const MIN_HISTORY = 2;

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
};

/**
 * Vendor months worth a look: more than 3x the median of that vendor's earlier
 * booked months. Annual vendors are skipped (one charge a year is not a spike),
 * and the running month is left out because it is month to date. Pure.
 */
export function vendorSpikes(
  vendors: Pick<ToolCostVendor, 'vendor' | 'byMonth' | 'annual'>[],
  months: string[],
  currentMonth: string | null,
): VendorSpike[] {
  const spikes: VendorSpike[] = [];
  for (const v of vendors) {
    if (v.annual) continue;
    const sorted = [...months].sort();
    for (let i = 0; i < sorted.length; i++) {
      const month = sorted[i]!;
      if (month === currentMonth) continue;
      const amount = v.byMonth[month] ?? 0;
      if (amount <= 0) continue;
      const earlier = sorted.slice(0, i).map((m) => v.byMonth[m] ?? 0).filter((x) => x > 0);
      if (earlier.length < MIN_HISTORY) continue;
      const usual = median(earlier);
      if (usual > 0 && amount >= SPIKE_FACTOR * usual && amount - usual >= SPIKE_MIN_EXCESS_EUR) {
        spikes.push({ vendor: v.vendor, month, amount, usual });
      }
    }
  }
  return spikes.sort((a, b) => b.amount - b.usual - (a.amount - a.usual));
}
